const withGuideFaqs = (guide) => ({
  ...guide,
  faqs: [
    {
      question: `How much does the ${guide.shortTitle} route usually cost?`,
      answer: `The total cost depends on your fuel type, vehicle consumption, toll exposure and the specific route option you choose. Whe2 is designed to help you compare those factors together instead of guessing from distance alone.`
    },
    {
      question: `Are tolls important on the ${guide.shortTitle} route?`,
      answer: `${guide.tolls}. On some trips, tolls may be a secondary factor; on others, they can materially change which route feels worthwhile.`
    },
    {
      question: `What is the best way to estimate the ${guide.shortTitle} trip properly?`,
      answer: `Use your real vehicle class, fuel type and average consumption, then compare route time, tolls and total trip cost together. That gives you a more decision-useful estimate than looking at distance by itself.`
    }
  ]
});

export const routeGuides = [
  {
    slug: 'johannesburg-to-cape-town-route-cost',
    path: '/guides/johannesburg-to-cape-town-route-cost',
    title: 'Johannesburg to Cape Town Route Cost Guide',
    shortTitle: 'Johannesburg to Cape Town',
    seoTitle: 'Johannesburg to Cape Town Route Cost Guide | Fuel, Tolls and Planning | Whe2',
    description: 'Plan a Johannesburg to Cape Town drive with clearer fuel cost, toll cost, distance and route comparison context.',
    intro: 'The Johannesburg to Cape Town drive is one of the most searched long-distance routes in South Africa. Cost can shift meaningfully depending on your vehicle, toll exposure and which route trade-off you choose.',
    distance: 'About 1,400 km depending on the route',
    duration: 'Roughly 14 to 16 hours of driving time',
    tolls: 'Tolls can materially affect total trip cost on faster route options',
    fuel: 'Fuel spend varies sharply by vehicle class, fuel type and consumption',
    whyItMatters: [
      'This is a high-distance route, so small fuel-consumption differences create large cost swings.',
      'The fastest route is not always the cheapest once tolls and fuel are considered together.',
      'Trip planning matters more here because overnight stops, departure times and return-leg planning often come into play.'
    ],
    planningPoints: [
      'Check fuel cost and toll cost together instead of looking at them separately.',
      'Compare route duration against total trip cost before choosing the fastest option by default.',
      'Use your actual vehicle consumption settings so long-distance estimates are not misleading.'
    ]
  },
  {
    slug: 'johannesburg-to-durban-route-cost',
    path: '/guides/johannesburg-to-durban-route-cost',
    title: 'Johannesburg to Durban Route Cost Guide',
    shortTitle: 'Johannesburg to Durban',
    seoTitle: 'Johannesburg to Durban Route Cost Guide | Fuel, Tolls and Planning | Whe2',
    description: 'Plan a Johannesburg to Durban drive with clearer fuel cost, toll cost, distance and route comparison context.',
    intro: 'Johannesburg to Durban is one of the most common intercity road trips in South Africa. It is short enough for same-day travel but still sensitive to toll-heavy route decisions and fuel assumptions.',
    distance: 'About 560 to 600 km depending on the route',
    duration: 'Roughly 5.5 to 7 hours of driving time',
    tolls: 'Toll plazas can be a major part of the trip cost on direct routes',
    fuel: 'Fuel cost is usually the other major cost driver and depends on your vehicle setup',
    whyItMatters: [
      'Tolls are highly visible on this route, so the cheapest route is not always the most obvious one.',
      'Travel-time differences can be modest while total cost differences are more noticeable.',
      'This route is popular for weekends and holidays, which makes planning before departure more valuable.'
    ],
    planningPoints: [
      'Compare total trip cost, not just distance, before choosing a route.',
      'Use your real vehicle consumption if you want the estimate to be decision-useful.',
      'Check whether saving time is worth the extra toll exposure for your specific trip.'
    ]
  },
  {
    slug: 'cape-town-to-gqeberha-route-cost',
    path: '/guides/cape-town-to-gqeberha-route-cost',
    title: 'Cape Town to Gqeberha Route Cost Guide',
    shortTitle: 'Cape Town to Gqeberha',
    seoTitle: 'Cape Town to Gqeberha Route Cost Guide | Fuel, Distance and Planning | Whe2',
    description: 'Plan a Cape Town to Gqeberha drive with clearer fuel cost, route distance and trip planning context.',
    intro: 'Cape Town to Gqeberha is a major long-distance coastal route in South Africa. It is often planned for holidays, family travel and regional business trips, which makes distance and fuel planning especially important.',
    distance: 'About 740 to 770 km depending on the route',
    duration: 'Roughly 8 to 9.5 hours of driving time',
    tolls: 'Toll exposure is usually lower than some inland long-distance routes',
    fuel: 'Fuel remains the main trip-cost driver for most light vehicles',
    whyItMatters: [
      'This route is long enough for fuel estimates to matter, even when tolls are limited.',
      'Departure time and stop planning can have a bigger effect on comfort than minor route changes.',
      'Holiday traffic can change the practical value of the fastest-looking route.'
    ],
    planningPoints: [
      'Use fuel consumption settings that reflect highway driving, not city assumptions.',
      'Compare route distance and travel time together before deciding on departure windows.',
      'Plan stops early if you are travelling with family or over a holiday period.'
    ]
  },
  {
    slug: 'pretoria-to-bloemfontein-route-cost',
    path: '/guides/pretoria-to-bloemfontein-route-cost',
    title: 'Pretoria to Bloemfontein Route Cost Guide',
    shortTitle: 'Pretoria to Bloemfontein',
    seoTitle: 'Pretoria to Bloemfontein Route Cost Guide | Fuel, Tolls and Planning | Whe2',
    description: 'Plan a Pretoria to Bloemfontein drive with clearer fuel cost, toll cost and route comparison context.',
    intro: 'Pretoria to Bloemfontein is a common central South Africa route used for business, family travel and longer intercity journeys. It is short enough for same-day travel but long enough for route cost trade-offs to matter.',
    distance: 'About 430 to 470 km depending on the route',
    duration: 'Roughly 4.5 to 5.5 hours of driving time',
    tolls: 'Toll costs can still influence the total cost on direct highway routes',
    fuel: 'Fuel cost remains a core part of the total route decision',
    whyItMatters: [
      'This route is often treated as straightforward, which can hide meaningful cost differences.',
      'Toll exposure can be noticeable relative to the total trip distance.',
      'It is frequently used as part of longer journeys, so planning accuracy has knock-on value.'
    ],
    planningPoints: [
      'Check toll and fuel totals together before treating the fastest option as the best one.',
      'Use your actual vehicle consumption to avoid underestimating trip cost.',
      'If this is one leg of a longer trip, plan the full route budget rather than this segment in isolation.'
    ]
  },
  {
    slug: 'johannesburg-to-polokwane-route-cost',
    path: '/guides/johannesburg-to-polokwane-route-cost',
    title: 'Johannesburg to Polokwane Route Cost Guide',
    shortTitle: 'Johannesburg to Polokwane',
    seoTitle: 'Johannesburg to Polokwane Route Cost Guide | Fuel, Tolls and Planning | Whe2',
    description: 'Plan a Johannesburg to Polokwane drive with clearer fuel cost, toll cost and route planning context.',
    intro: 'Johannesburg to Polokwane is a popular northbound route in South Africa for family travel, logistics movement and regional business trips. It is a route where tolls, drive time and fuel efficiency all deserve attention.',
    distance: 'About 320 to 360 km depending on the route',
    duration: 'Roughly 3.5 to 4.5 hours of driving time',
    tolls: 'Tolls can be a visible part of total trip cost on direct routes',
    fuel: 'Fuel cost is still material, especially for larger vehicles and return trips',
    whyItMatters: [
      'This route is common enough that many drivers underestimate how much small cost differences add up over repeated travel.',
      'Toll exposure can be meaningful for commuters, business users and frequent family trips.',
      'Return-trip planning changes the real budget picture more than the one-way estimate alone.'
    ],
    planningPoints: [
      'Look at return-trip cost if this route is part of a same-day or weekend plan.',
      'Compare toll cost against time saved rather than assuming the direct route is automatically best.',
      'Use your real vehicle class and fuel assumptions for more useful planning.'
    ]
  },
  {
    slug: 'durban-to-cape-town-route-cost',
    path: '/guides/durban-to-cape-town-route-cost',
    title: 'Durban to Cape Town Route Cost Guide',
    shortTitle: 'Durban to Cape Town',
    seoTitle: 'Durban to Cape Town Route Cost Guide | Fuel, Tolls and Planning | Whe2',
    description: 'Plan a Durban to Cape Town drive with clearer fuel cost, toll cost, distance and route planning context.',
    intro: 'Durban to Cape Town is one of the longest mainstream intercity drives in South Africa. Because it covers major distance, total trip cost is influenced heavily by fuel, route choice and the pace at which you plan the journey.',
    distance: 'About 1,600 km depending on the route',
    duration: 'Roughly 16 to 19 hours of driving time',
    tolls: 'Toll costs can add up on faster or more direct options',
    fuel: 'Fuel is usually the dominant trip-cost component on this route',
    whyItMatters: [
      'This route is long enough that fuel assumptions can change the final budget materially.',
      'Drivers often split the trip over multiple legs, so planning mistakes compound.',
      'The cheapest route and the most comfortable route are not always the same.'
    ],
    planningPoints: [
      'Plan the drive as a full-trip budget, not only as a one-way route estimate.',
      'Check fuel and toll totals together before deciding how much time you want to save.',
      'Use realistic consumption and stop assumptions if you are travelling with family or luggage.'
    ]
  },
  {
    slug: 'johannesburg-to-mbombela-route-cost',
    path: '/guides/johannesburg-to-mbombela-route-cost',
    title: 'Johannesburg to Mbombela Route Cost Guide',
    shortTitle: 'Johannesburg to Mbombela',
    seoTitle: 'Johannesburg to Mbombela Route Cost Guide | Fuel, Tolls and Planning | Whe2',
    description: 'Plan a Johannesburg to Mbombela drive with clearer fuel cost, toll cost and route planning context.',
    intro: 'Johannesburg to Mbombela is a high-intent South African route used for family trips, tourism and regional business travel. It is long enough for tolls and fuel to matter, but short enough that route choice can still feel deceptively simple.',
    distance: 'About 340 to 380 km depending on the route',
    duration: 'Roughly 4 to 5 hours of driving time',
    tolls: 'Tolls can be a meaningful part of total trip cost on direct routes',
    fuel: 'Fuel cost is still a visible factor, especially for return travel',
    whyItMatters: [
      'This route is frequently used for weekend and holiday travel, so costs repeat over time.',
      'Tolls can be large enough to affect which route feels worth it.',
      'Return-trip thinking matters because many travellers make this as a round trip.'
    ],
    planningPoints: [
      'Compare route time against toll cost instead of assuming the fastest route is best.',
      'Estimate the return trip as part of the same decision.',
      'Use your actual vehicle settings if you want a realistic total route cost.'
    ]
  },
  {
    slug: 'cape-town-to-george-route-cost',
    path: '/guides/cape-town-to-george-route-cost',
    title: 'Cape Town to George Route Cost Guide',
    shortTitle: 'Cape Town to George',
    seoTitle: 'Cape Town to George Route Cost Guide | Fuel, Distance and Planning | Whe2',
    description: 'Plan a Cape Town to George drive with clearer fuel cost, distance and route planning context.',
    intro: 'Cape Town to George is a popular Western Cape route for leisure travel, family trips and holiday planning. It is not only about distance; departure timing, stop planning and realistic fuel estimates also shape the trip.',
    distance: 'About 430 to 450 km depending on the route',
    duration: 'Roughly 4.5 to 5.5 hours of driving time',
    tolls: 'Toll exposure is usually lower than many inland long-distance routes',
    fuel: 'Fuel is the main variable cost for most vehicles on this route',
    whyItMatters: [
      'The route is common enough that drivers often underestimate the value of planning ahead.',
      'Holiday traffic and stop timing can matter almost as much as the route itself.',
      'Fuel assumptions have a visible effect because this is a frequent repeat-trip corridor.'
    ],
    planningPoints: [
      'Use realistic highway consumption rather than default assumptions.',
      'Plan departure time and stops alongside total route cost.',
      'If this is a return journey, compare the full-trip fuel cost before you leave.'
    ]
  }
].map(withGuideFaqs);

export const routeGuideMap = Object.fromEntries(routeGuides.map((guide) => [guide.slug, guide]));
